// Project switcher at the top of the sidebar: search, recent projects and a
// link to the workspace-wide "All projects" page.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import type { Project } from '@goblic/shared'
import { useCurrentProject } from '../lib/project-context'
import { NewProjectDialog, useCanCreateProject } from './projects/new-project-dialog'

/** Sections that exist for every project, so switching keeps the one you're in. */
const SECTIONS = ['overview', 'boards', 'tasks', 'runs', 'reports', 'knowledge', 'settings'] as const
type Section = (typeof SECTIONS)[number]

function sectionOf(pathname: string): Section {
  const match = /^\/p\/[^/]+\/([^/]+)/.exec(pathname)
  const found = SECTIONS.find((s) => s === match?.[1])
  return found ?? 'overview'
}

export function ProjectSwitcher() {
  const { project, projects, recentKeys, setCurrentProject } = useCurrentProject()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const canCreate = useCanCreateProject()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const needle = query.trim().toLowerCase()
  const matches = useMemo(
    () =>
      needle
        ? projects.filter(
            (p) => p.name.toLowerCase().includes(needle) || p.key.toLowerCase().includes(needle)
          )
        : projects,
    [projects, needle]
  )
  const recent = useMemo(
    () =>
      needle
        ? []
        : recentKeys
            .map((key) => projects.find((p) => p.key === key))
            .filter((p): p is Project => p != null && p.key !== project?.key)
            .slice(0, 3),
    [needle, recentKeys, projects, project]
  )

  function choose(next: Project): void {
    setCurrentProject(next.key)
    setOpen(false)
    setQuery('')
    const section = sectionOf(pathname)
    // Streams differ per project, so the board goes through its redirect.
    switch (section) {
      case 'boards':
        void navigate({ to: '/p/$projectKey/boards', params: { projectKey: next.key } })
        break
      case 'tasks':
        void navigate({ to: '/p/$projectKey/tasks', params: { projectKey: next.key }, search: {} })
        break
      case 'runs':
        void navigate({ to: '/p/$projectKey/runs', params: { projectKey: next.key } })
        break
      case 'reports':
        void navigate({ to: '/p/$projectKey/reports', params: { projectKey: next.key } })
        break
      case 'knowledge':
        void navigate({ to: '/p/$projectKey/knowledge', params: { projectKey: next.key } })
        break
      case 'settings':
        void navigate({ to: '/p/$projectKey/settings', params: { projectKey: next.key }, search: {} })
        break
      default:
        void navigate({ to: '/p/$projectKey/overview', params: { projectKey: next.key } })
    }
  }

  const row = (p: Project) => (
    <li key={p.id}>
      <button
        type="button"
        onClick={() => choose(p)}
        className={`flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-base hover:bg-sunken ${
          p.id === project?.id ? 'text-moss-strong' : 'text-ink'
        }`}
      >
        <span className="w-12 shrink-0 font-mono text-sm text-ink-3">{p.key}</span>
        <span className="min-w-0 truncate">{p.name}</span>
      </button>
    </li>
  )

  return (
    <div ref={rootRef} className="relative px-3 pb-1">
      <button
        ref={triggerRef}
        type="button"
        className="flex w-full items-center gap-2 rounded-control border border-line-strong bg-surface px-2.5 py-2 text-left hover:bg-sunken"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="rounded bg-moss-soft px-1.5 py-0.5 font-mono text-sm font-semibold text-moss-strong">
          {project?.key ?? '—'}
        </span>
        <span className="min-w-0 flex-1 truncate text-base font-medium text-ink">
          {project?.name ?? 'No project'}
        </span>
        <span aria-hidden className="text-ink-3">
          ▾
        </span>
      </button>
      {open ? (
        <div className="absolute inset-x-3 top-full z-40 mt-1 rounded-panel border border-line bg-surface p-2 shadow-overlay">
          <input
            className="field-input mb-2"
            placeholder="Find a project…"
            aria-label="Find a project"
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="max-h-72 overflow-y-auto">
            {recent.length > 0 ? (
              <>
                <p className="px-2 pb-1 text-sm text-ink-3">Recent</p>
                <ul className="mb-2">{recent.map(row)}</ul>
                <p className="px-2 pb-1 text-sm text-ink-3">All</p>
              </>
            ) : null}
            {matches.length > 0 ? (
              <ul>{matches.map(row)}</ul>
            ) : (
              <p className="px-2 py-2 text-sm text-ink-3">No project matches.</p>
            )}
          </div>
          <div className="mt-2 border-t border-line pt-2">
            <Link
              to="/dashboard"
              className="block rounded-control px-2 py-1.5 text-base text-ink-2 hover:bg-sunken hover:text-ink"
            >
              All projects
            </Link>
            {canCreate ? (
              <button
                type="button"
                className="block w-full rounded-control px-2 py-1.5 text-left text-base text-ink-2 hover:bg-sunken hover:text-ink"
                onClick={() => {
                  setOpen(false)
                  setCreating(true)
                }}
              >
                New project
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <NewProjectDialog
        open={creating}
        onClose={() => {
          setCreating(false)
          // The menu closed (and its button unmounted) before the dialog opened.
          triggerRef.current?.focus()
        }}
      />
    </div>
  )
}
